import { useCallback, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useWorkspace } from "#app/lib/data/workspace-context";

/**
 * Workspace-aware navigation that works from anywhere — screens, sheets,
 * portals. Builds fully-qualified hrefs under the active workspace so
 * tab/route switches resolve correctly regardless of navigation context.
 *
 * Usage:
 * ```ts
 * const { push, replace } = useAppNavigate();
 * push("/map");                        // navigates to map tab
 * push("/settings/teams");             // navigates to teams settings
 * replace("/settings/teams");          // replaces current route (back nav)
 * ```
 */
export function useAppNavigate() {
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const wp = activeWorkspace?.urlPath ?? "";

  // Split an optional `?query` off the route and turn it into a real `search`
  // object. TanStack Router treats `to` as a pathname ONLY — a query string
  // embedded in `to` is never parsed into typed search params, so navigating to
  // `/map?focusLat=…` (especially map→map) silently drops the params and does
  // nothing. Search must be passed via the `search` option.
  const buildOptions = useCallback(
    (route: string) => {
      const queryIndex = route.indexOf("?");
      const path = queryIndex === -1 ? route : route.slice(0, queryIndex);
      const to = (path === "/" ? `/${wp}` : `/${wp}${path}`) as unknown as "/";
      if (queryIndex === -1) return { to };
      const search = Object.fromEntries(
        new URLSearchParams(route.slice(queryIndex + 1)),
      );
      return { to, search: search as unknown as never };
    },
    [wp],
  );

  return useMemo(
    () => ({
      push: (route: string) => navigate(buildOptions(route)),
      replace: (route: string) =>
        navigate({ ...buildOptions(route), replace: true }),
    }),
    [navigate, buildOptions],
  );
}
